import React, { useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import Card from "./Card";
import SocChartCard, { SocEmptyFrame } from "./SocChartCard";
import { FOCUS_RING } from "./socUi";
import { formatAge, formatLocal } from "./socFreshness";
import { severityLabel } from "./socQueue";
import { AXIS, GRID, HEAT, SEVERITY, VIVID } from "./socChartTheme";
import { ActivityTooltip } from "./SocTrendCharts";
import { WEEKDAYS, ageBuckets, arrivalMatrix, heatLevel, newestRows } from "./socConsole";

const Header = ({ title, subtitle, aside }) => (
  <div className="flex items-start justify-between gap-2">
    <div className="min-w-0">
      <h3 className="font-semibold text-content-primary">{title}</h3>
      {subtitle && <p className="text-xs text-content-muted">{subtitle}</p>}
    </div>
    {aside}
  </div>
);

const hh = (h) => String(h).padStart(2, "0");

// When the open items arrived: weekday x hour of creation in the viewer's time, one shade per cell
// against the busiest cell. Pointing at (or focusing) a cell lifts it, lights its weekday and hour
// labels, dims the cells outside its row and column, and the readout under the grid names it.
export const SocArrivalHeatmap = ({ rows }) => {
  const [hover, setHover] = useState(null); // { d, h } | null
  const m = arrivalMatrix(rows);
  const max = Math.max(0, ...m.flat());
  const total = m.flat().reduce((a, b) => a + b, 0);
  let busiest = null;
  m.forEach((hours, d) => hours.forEach((v, h) => { if (v > 0 && (!busiest || v > busiest.v)) busiest = { d, h, v }; }));

  return (
    <div data-testid="soc-chart-heatmap" className="h-full">
      <Card className="h-full">
        <Header title="When open items arrived" subtitle="Open queue items by the weekday and hour Axaris first recorded them, your time" />
        {(
          <>
            <div className="relative mt-3 overflow-x-auto p-1">
              {total === 0 && (
                <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center" data-testid="soc-heatmap-empty">
                  <p className="rounded-full bg-theme-secondary px-3 py-1.5 text-sm text-content-secondary shadow-sm">No open items.</p>
                </div>
              )}
              <div
                className="grid min-w-[30rem] gap-[3px] text-[10px] text-content-muted"
                style={{ gridTemplateColumns: "2.2rem repeat(24, minmax(0, 1fr))" }}
                onMouseLeave={() => setHover(null)}
              >
                <span />
                {Array.from({ length: 24 }, (_, h) => {
                  const on = hover?.h === h;
                  return (
                    <span key={h} data-testid={`soc-heat-hour-${h}`}
                      className={`text-center transition-colors ${on ? "font-bold text-accent-cyan" : ""}`}>
                      {on || h % 3 === 0 ? h : ""}
                    </span>
                  );
                })}
                {WEEKDAYS.map((day, d) => (
                  <React.Fragment key={day}>
                    <span data-testid={`soc-heat-day-${d}`}
                      className={`flex items-center transition-colors ${hover?.d === d ? "font-bold text-accent-cyan" : ""}`}>
                      {day}
                    </span>
                    {m[d].map((v, h) => {
                      const isOn = hover?.d === d && hover?.h === h;
                      const inCross = hover && (hover.d === d || hover.h === h);
                      return (
                        <span
                          key={h}
                          tabIndex={0}
                          role="img"
                          aria-label={`${day} ${hh(h)}:00, ${v} open ${v === 1 ? "item" : "items"}`}
                          data-testid={`soc-heat-${d}-${h}`}
                          data-count={v}
                          onMouseEnter={() => setHover({ d, h })}
                          onFocus={() => setHover({ d, h })}
                          onBlur={() => setHover(null)}
                          className={`relative aspect-square cursor-default rounded-[3px] transition-[transform,opacity,box-shadow] duration-150 focus:outline-none ${
                            isOn ? "z-10 scale-125 shadow-md ring-2 ring-accent-cyan" : ""
                          } ${hover && !inCross ? "opacity-40" : ""}`}
                          style={{ background: HEAT[heatLevel(v, max)] }}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-content-muted">
              {hover ? (
                <span data-testid="soc-heatmap-readout" aria-live="polite" className="font-semibold text-content-primary">
                  {(() => {
                    const v = m[hover.d][hover.h];
                    const share = total > 0 ? Math.round((v / total) * 100) : 0;
                    return `${WEEKDAYS[hover.d]} ${hh(hover.h)}:00–${hh((hover.h + 1) % 24)}:00 · ${v} open ${v === 1 ? "item" : "items"}${v > 0 ? ` (${share}% of all open)` : ""}`;
                  })()}
                </span>
              ) : (
                <span data-testid="soc-heatmap-busiest">
                  {busiest ? `Busiest: ${WEEKDAYS[busiest.d]} ${hh(busiest.h)}:00 (${busiest.v}) · point at a cell for details` : "No open items"}
                </span>
              )}
              <span className="flex items-center gap-1">
                Fewer {HEAT.map((c) => <i key={c} className="inline-block h-2.5 w-3.5 rounded-sm" style={{ background: c }} />)} More
              </span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

// Newest open items across every queued source; each opens its details in the queue. Pointing at
// (or focusing) a row marks it with its severity colour, nudges it right, shows "Details" and fades
// the other rows.
export const SocNewestFeed = ({ rows, onOpen }) => {
  const [activeId, setActiveId] = useState(null);
  const items = newestRows(rows, 8);
  return (
    <div data-testid="soc-feed" className="h-full">
      <Card className="h-full">
        <Header title="Newest open items" subtitle="Across every source the queue covers" />
        {items.length === 0 ? (
          <SocEmptyFrame height="h-48" testId="soc-feed-empty">No open items.</SocEmptyFrame>
        ) : (
          <ol className="mt-2 space-y-0.5" onMouseLeave={() => setActiveId(null)}>
            {items.map((r) => {
              const color = SEVERITY[r.severity] || "var(--text-muted)";
              const on = activeId === r.id;
              return (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => onOpen(r)}
                    onMouseEnter={() => setActiveId(r.id)}
                    onFocus={() => setActiveId(r.id)}
                    onBlur={() => setActiveId(null)}
                    data-testid={`soc-feed-${r.id}`}
                    title={`${r.title}${r.created ? ` · created ${formatLocal(r.created)}` : ""}`}
                    style={{ "--soc-feed-accent": color }}
                    className={`grid w-full grid-cols-[3.2rem_0.7rem_minmax(0,1fr)_auto] items-start gap-2 rounded-lg border-l-[3px] px-1.5 py-1.5 text-left text-[13px] transition-[transform,opacity,background-color] duration-150 ${FOCUS_RING} ${
                      on ? "border-l-[color:var(--soc-feed-accent)] bg-theme-hover shadow-sm motion-safe:translate-x-0.5" : "border-l-transparent"
                    } ${activeId && !on ? "opacity-50" : ""}`}
                  >
                    <span className="pt-px text-[11px] tabular-nums text-content-muted">{r.ageMs != null ? formatAge(r.ageMs) : "—"}</span>
                    <span
                      className={`mt-1 h-2.5 w-2.5 rounded-full transition-transform duration-150 ${on ? "scale-125" : ""}`}
                      style={{ background: color, boxShadow: on ? `0 0 0 3px var(--bg-card-solid), 0 0 0 4px ${color}` : undefined }}
                      aria-hidden="true"
                    />
                    <span className="min-w-0">
                      <span className={`block truncate font-medium transition-colors ${on ? "text-accent-cyan" : "text-content-primary"}`}>{r.title}</span>
                      <span className="block truncate text-xs text-content-muted">{`${r.source} · ${severityLabel(r.severity)}`}</span>
                    </span>
                    <span
                      className={`self-center whitespace-nowrap text-[11px] font-semibold text-accent-cyan transition-opacity ${on ? "opacity-100" : "opacity-0"}`}
                      aria-hidden="true"
                    >
                      Details →
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        )}
      </Card>
    </div>
  );
};

const OWNER_SERIES = [
  { field: "unassigned", label: "Unassigned", color: VIVID.rose },
  { field: "assigned", label: "Assigned", color: VIVID.blue },
];

// Queue health: open items by age, split by owner. Pointing at a row shows its counts; pointing at a
// series' bars, or at (or focusing) its legend entry, keeps that series and fades the other.
export const SocQueueAgeChart = ({ rows }) => {
  const [activeField, setActiveField] = useState(null);
  const data = ageBuckets(rows);
  const total = data.reduce((s, d) => s + d.assigned + d.unassigned, 0);
  const faded = (field) => !!activeField && activeField !== field;
  return (
    <SocChartCard
      testId="soc-chart-queue-age"
      title="Queue health"
      subtitle="Open items by age and owner"
      empty={total === 0}
      emptyText="No open items."
      legend={OWNER_SERIES.map(({ label, color }) => ({ label, color }))}
      activeLegend={OWNER_SERIES.find((s) => s.field === activeField)?.label ?? null}
      onLegendHover={(label) => setActiveField(OWNER_SERIES.find((s) => s.label === label)?.field ?? null)}
      table={{ columns: ["Age", "Unassigned", "Assigned"], rows: data.map((d) => [d.label, d.unassigned, d.assigned]) }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 4 }}>
          <CartesianGrid {...GRID} vertical horizontal={false} />
          <XAxis type="number" allowDecimals={false} {...AXIS} />
          <YAxis type="category" dataKey="label" width={84} {...AXIS} />
          <Tooltip
            content={<ActivityTooltip series={OWNER_SERIES} emptyText="No open items" />}
            cursor={{ fill: "var(--accent-cyan-muted)" }}
          />
          <Bar dataKey="unassigned" name="Unassigned" stackId="a" fill={VIVID.rose}
            fillOpacity={faded("unassigned") ? 0.2 : 1} isAnimationActive={false}
            onMouseEnter={() => setActiveField("unassigned")} onMouseLeave={() => setActiveField(null)} />
          <Bar dataKey="assigned" name="Assigned" stackId="a" fill={VIVID.blue} radius={[0, 4, 4, 0]}
            fillOpacity={faded("assigned") ? 0.2 : 1} isAnimationActive={false}
            onMouseEnter={() => setActiveField("assigned")} onMouseLeave={() => setActiveField(null)} />
        </BarChart>
      </ResponsiveContainer>
    </SocChartCard>
  );
};

const TOP_SEVERITY = [
  ["critical", "Critical", "var(--soc-v-rose)"],
  ["high", "High", "var(--soc-v-orange)"],
  ["medium", "Medium", "var(--soc-v-amber)"],
  ["low", "Low", "var(--soc-v-slate)"],
];

// "4 open: 1 Critical · 3 Medium" - the severities behind a row's count, worst first.
const severityBreakdown = (r) => TOP_SEVERITY
  .filter(([key]) => (r.severity?.[key] || 0) > 0)
  .map(([key, label]) => `${r.severity[key]} ${label}`)
  .join(" · ");

// Most exposed identities or devices: ranked bars by open findings, coloured by the worst severity
// among them; each row opens the object in GRC > Assets. Pointing at (or focusing) a row lights it,
// splits its bar into its severities, fades the other rows and spells the breakdown out under the
// list. Left out when the list is empty or can't be read (the page only asks when the inventory is
// connected).
// `openId`: the row whose details are open under the cards (SocExposedDetails); its hint reads Hide.
export const SocTopExposed = ({ kind, rows, onOpen, emptyText, openId = null }) => {
  const [activeId, setActiveId] = useState(null);
  const isIdentity = kind === "identities";
  if (!rows || rows.length === 0) {
    return (
      <div data-testid={`soc-top-${kind}`} className="h-full">
        <Card className="h-full">
          <Header
            title={isIdentity ? "Most exposed identities" : "Most exposed devices"}
            subtitle={`Open findings linked to each ${isIdentity ? "identity" : "device"}`}
          />
          <SocEmptyFrame height="h-48" testId={`soc-top-${kind}-empty`}>
            {emptyText || `No ${isIdentity ? "identity" : "device"} has open findings.`}
          </SocEmptyFrame>
        </Card>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.openFindings || 0), 1);
  const on = rows.find((r) => r.id === activeId) || null;
  return (
    <div data-testid={`soc-top-${kind}`} className="h-full">
      <Card className="h-full">
        <Header
          title={isIdentity ? "Most exposed identities" : "Most exposed devices"}
          subtitle={`Open findings linked to each ${isIdentity ? "identity" : "device"}`}
        />
        <ol className="mt-3 space-y-2" onMouseLeave={() => setActiveId(null)}>
          {rows.map((r) => {
            const worst = TOP_SEVERITY.find(([key]) => (r.severity?.[key] || 0) > 0);
            const color = worst ? worst[2] : "var(--soc-v-slate)";
            const isOpen = openId != null && String(r.id) === String(openId);
            const lit = r.id === activeId || isOpen;
            const counted = TOP_SEVERITY.filter(([key]) => (r.severity?.[key] || 0) > 0);
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => onOpen(r)}
                  onMouseEnter={() => setActiveId(r.id)}
                  onFocus={() => setActiveId(r.id)}
                  onBlur={() => setActiveId(null)}
                  data-testid={`soc-top-${kind}-${r.id}`}
                  aria-expanded={isOpen}
                  title={severityBreakdown(r)}
                  className={`w-full rounded-lg px-1.5 py-1 text-left transition-[opacity,background-color,box-shadow] duration-150 ${FOCUS_RING} ${
                    lit ? "bg-theme-hover shadow-sm" : ""
                  } ${activeId && !lit ? "opacity-40" : ""}`}
                >
                  <span className="flex items-baseline gap-2 text-[13px]">
                    <span className={`min-w-0 flex-1 truncate font-medium transition-colors ${lit ? "text-accent-cyan" : "text-content-primary"}`}>
                      {r.name}
                      {r.detail && <span className="ml-1.5 text-xs font-normal text-content-muted">{r.detail}</span>}
                    </span>
                    <span className={`shrink-0 text-[11px] font-semibold text-accent-cyan transition-opacity ${lit ? "opacity-100" : "opacity-0"}`} aria-hidden="true">
                      {isOpen ? "Hide" : "Open →"}
                    </span>
                    <span className="shrink-0 font-bold tabular-nums text-content-primary">{r.openFindings}</span>
                  </span>
                  <span
                    className={`mt-1 block overflow-hidden rounded-full bg-theme-secondary transition-[height] duration-150 ${lit ? "h-2.5" : "h-1.5"}`}
                    aria-hidden="true"
                  >
                    {lit && counted.length > 0 ? (
                      // The same bar split into its severities, worst first.
                      <span className="flex h-full" style={{ width: `${(r.openFindings / max) * 100}%` }} data-testid={`soc-top-${kind}-${r.id}-split`}>
                        {counted.map(([key, , c]) => (
                          <span key={key} className="block h-full" style={{ flex: r.severity[key], background: c }} />
                        ))}
                      </span>
                    ) : (
                      <span className="block h-full rounded-full" style={{ width: `${(r.openFindings / max) * 100}%`, background: color }} />
                    )}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
        <p className="mt-2 min-h-[1.25rem] text-xs" data-testid={`soc-top-${kind}-readout`} aria-live="polite">
          {on ? (
            <>
              <span className="font-semibold text-content-primary">{on.name}</span>
              <span className="text-content-secondary">{` · ${on.openFindings} open${severityBreakdown(on) ? `: ${severityBreakdown(on)}` : ""}`}</span>
            </>
          ) : (
            <span className="text-content-muted">{`Point at ${isIdentity ? "an identity" : "a device"} to see its findings by severity`}</span>
          )}
        </p>
      </Card>
    </div>
  );
};
